import { Box, Stack, Typography } from '@mui/material'
import { FC, useCallback, useMemo } from 'react'
import { GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { HomeOutlined, ShoppingCartOutlined } from '@mui/icons-material'

import { ActionPage } from 'components/common/ActionPage/ActionPage.component'
import { AddressMap } from 'components/common/AdressMap'
import { CreateUpdateProperty } from '../PurchaseFlow/steps/AddressStep/CreateUpdateProperty'
import { FilterTab } from 'components/common/FilterTab'
import { GoogleAPIPlace } from 'models/purchaseFlow'
import { Label } from 'components/common/Label'
import { LoadMapScriptWrapper } from 'components/common/AdressMap/LoadMapScriptWrapper'
import { MUIButton } from 'components/common/MUIButton'
import { Path } from 'constants/router'
import { PropertyType } from 'models/property'
import i18n from 'translations/i18n'
import { isAddressValid } from 'utils/validators'
import { useNavigate } from 'react-router-dom'
import { useNewProperty } from './NewProperty.context'
import { usePurchaseFlowRealEstateProperty } from '../PurchaseFlow/_main/contexts'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

/**
 * New Property controller
 * @example <NewPropertyController />
 */
export const NewPropertyController: FC = () => {
  const { t } = useTranslation(['new_property'])
  const navigate = useNavigate()

  const { clientData } = useUserData()

  const {
    selectedPropertyType,
    setSelectedPropertyType,
    selectedPlace,
    setSelectedPlace,
    submitCreateNewProperty,
    createNewProperty,
    isCreatePropertyDisabled,
  } = useNewProperty()

  const { isAddressManually } = usePurchaseFlowRealEstateProperty()

  const addressIsValid = useMemo(() => isAddressValid(selectedPlace), [selectedPlace])

  const handleMapOnChange = useCallback((newGooglePlace: GoogleAPIPlace | null) => {
    setSelectedPlace(newGooglePlace)
  }, [setSelectedPlace])

  const onActionPageClose = useCallback(() => {
    navigate(Path.INDEX)
  }, [navigate])

  return (
    <ActionPage
      id="new-property-page"
      title={t('title')}
      onClose={onActionPageClose}
      footerChildren={
        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          height="100%"
          padding="0 2rem"
        >
          <MUIButton
            isLoading={createNewProperty.isPending}
            onClick={() => submitCreateNewProperty()}
            disabled={isCreatePropertyDisabled}
          >
            {t('create_property_button')}
          </MUIButton>
        </Stack>
      }
    >
      <LoadMapScriptWrapper
        language={i18n.language}
        countryCode={clientData?.country}
      >
        <Box
          flex="1 0 350px"
          height="100%" sx={{ overflowY: 'auto' }}
          paddingLeft={4}
          paddingRight={2}
          paddingTop={4.8}
          boxSizing="border-box"
          position="relative"
        >
          <Stack flexDirection="row" justifyContent="space-between" gap="6rem" height="100%">

            <Stack gap=".4rem" width="40%" position="relative" overflow="auto" paddingBottom="4rem">

              <Typography marginBottom="3.2rem" color={GRAY_900} variant='text-xl' fontWeight={600}>
                {t('property_information')}
              </Typography>

              <CreateUpdateProperty
                addressIsValid={addressIsValid}
                handleMapOnChange={handleMapOnChange}
                selectedPlace={selectedPlace}
                setSelectedPlace={setSelectedPlace}
              />

              <Stack gap=".6rem">
                <Typography marginTop="3.2rem" color={GRAY_700} variant='text-sm' fontWeight={500}>
                  {t('destination')}
                </Typography>

                <Stack gap={0.8} flexDirection="row" width="100%">
                  {Object.values(PropertyType)
                    .sort((a, b) => (a === PropertyType.RESIDENTIAL ? -1 : b === PropertyType.RESIDENTIAL ? 1 : 0))
                    .map((type) => (
                      <FilterTab
                        key={type}
                        sx={{
                          width: '100%',
                          gap: '.8rem',
                          justifyContent: 'center',
                          padding: '1.6rem 1.2rem'
                        }}
                        height="8.4rem"
                        isActive={selectedPropertyType === type}
                        onClick={() => setSelectedPropertyType(type)}
                        label={
                          <Stack gap=".6rem" justifyContent="center" alignItems="center">
                            {type === PropertyType.RESIDENTIAL
                              ? <HomeOutlined fontSize='large' />
                              : <ShoppingCartOutlined fontSize='large' />
                            }
                            <Typography color={GRAY_900} variant='text-sm'>
                              {t(`property:client.information_page.destination.${type}`)}
                            </Typography>
                          </Stack>
                        }
                      />
                    ))}
                </Stack>
              </Stack>

            </Stack>

            <Stack
              sx={{
                top: 0,
                right: 4,
                left: '47%',
                width: '50%',
                height: '70vh',
                paddingTop: 4.8,
                overflow: 'hidden',
                position: 'absolute',
              }}
            >

              {isAddressManually &&
                <Box paddingBottom=".8rem">
                  <Label text={t('order:step_address.manually_drag_map_pin_note')} />
                </Box>
              }

              <AddressMap
                height="100%"
                draggable={true} // New property page always allow drag pin because user don't need to select existing property
                language={i18n.language}
                countryCode={clientData?.country}
                isMapPinAdjusted={!isAddressManually}
                handleChange={handleMapOnChange}
              />

            </Stack>

          </Stack>
        </Box>
      </LoadMapScriptWrapper>
    </ActionPage>
  )
}
